import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, Divider, Box, Typography, Paper } from "@mui/material";
import MailIcon from "@mui/icons-material/MailOutline";
import PropTypes from "prop-types";
import MDAvatar from "components/MDAvatar";
import MDButton from "components/MDButton";
import { useAuth } from "context";
import config from "config/config";
import cateringIcon from "../../../assets/images/icons/catering-icons/cr-logo-black.png";

const BASE_URL = config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"].API_BASE_URL;

const LABELS = {
  marketing: {
    title: "Marketing Emails",
    description: "Promotional content such as offers, newsletters, and updates.",
  },
  transactional: {
    title: "Transactional Emails",
    description: "Critical updates such as receipts, confirmations, and alerts.",
  },
};

const editableKeys = ["marketing"];

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [initialPreferences, setInitialPreferences] = useState(null);
  const [saving, setSaving] = useState(false);
  const { showSnackbarWithMessage } = useAuth();
  const token = searchParams.get("token");

  useEffect(() => {
    const action = searchParams.get("action");
    if (!token) {
      navigate("/");
      return;
    }

    const fetchData = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/v1/notifications/unsubscribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, action }),
        });
        const json = await res.json();

        if (res.ok && json.success) {
          setStatus("success");
          setData(json);
          if (json.data.action === "preference" && json.data.preferences) {
            setPreferences({
              email: {
                transactional: !!json.data.preferences.email.transactional,
                marketing: !!json.data.preferences.email.marketing,
              },
            });
            setInitialPreferences({
              email: {
                transactional: !!json.data.preferences.email.transactional,
                marketing: !!json.data.preferences.email.marketing,
              },
            });
          } else {
            showSnackbarWithMessage({
              type: "success",
              message: json.message,
            });
          }
        } else {
          throw new Error(json.message);
        }
      } catch {
        setStatus("error");
        setData(null);
        showSnackbarWithMessage({
          type: "error",
          message: "We were unable to update your email preferences. Please try again later.",
        });
      }
    };

    fetchData();
  }, [token]);

  const setPreference = (key, value) => {
    setPreferences((prev) => (prev ? { email: { ...prev.email, [key]: value } } : prev));
  };

  const handleSave = async ({ unsubscribeAll = false, restoreAll = false } = {}) => {
    if (!token || saving) return;

    const isDirty =
      preferences &&
      initialPreferences &&
      editableKeys.some((key) => preferences.email[key] !== initialPreferences.email[key]);

    if (!unsubscribeAll && !restoreAll && !isDirty) {
      return;
    }

    setSaving(true);

    const body = {
      token,
      preferences: unsubscribeAll || restoreAll ? null : preferences,
      consent_source: "EMAIL_OPT_IN_OUT",
      unsubscribeAll,
      restoreAll,
    };

    try {
      const res = await fetch(`${BASE_URL}/api/v1/notifications/preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();

      if (res.ok && json.success) {
        setData(json);
        if (json.data.action === "preference" && json.data.preferences) {
          setPreferences({
            email: {
              transactional: !!json.data.preferences.email.transactional,
              marketing: !!json.data.preferences.email.marketing,
            },
          });
          setInitialPreferences({
            email: {
              transactional: !!json.data.preferences.email.transactional,
              marketing: !!json.data.preferences.email.marketing,
            },
          });
        }
        showSnackbarWithMessage({ type: "success", message: json.message });
        navigate(`?token=${token}&action=preference`, { replace: true });
      } else {
        throw new Error(json.message);
      }
    } catch {
      showSnackbarWithMessage({
        type: "error",
        message: "We were unable to update your email preferences. Please try again later.",
      });
    } finally {
      setSaving(false);
    }
  };

  const Header = () => (
    <Paper
      elevation={2}
      sx={{
        position: "fixed",
        top: 16,
        left: 16,
        right: 16,
        backgroundColor: "#fff",
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        px: 4,
        borderRadius: 2,
        zIndex: 10,
      }}
    >
      <MDAvatar
        src={cateringIcon}
        sx={{
          borderRadius: 0,
          width: "118px",
          height: "auto",
          objectFit: "contain",
        }}
      />
    </Paper>
  );

  const Container = ({ children }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#F0FDF4",
        px: 2,
        pb: 6,
        pt: { xs: "100px", sm: "100px" },
        boxSizing: "border-box",
      }}
    >
      {children}
    </Box>
  );

  Container.propTypes = {
    children: PropTypes.node.isRequired,
  };

  if (status === "loading")
    return (
      <>
        <Header />
        <Container>
          <Typography align="center">Loading your preferences...</Typography>
        </Container>
      </>
    );

  // Unsubscribe Success
  if (status === "success" && data?.data?.action === "unsubscribe") {
    return (
      <>
        <Header />
        <Container>
          <Card
            sx={{
              maxWidth: 480,
              p: 4,
              textAlign: "center",
              borderRadius: 4,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 3,
              }}
            >
              <MailIcon color="success" sx={{ fontSize: "48px !important" }} />
            </Box>

            <Typography variant="h5" fontWeight={600}>
              You’ve unsubscribed
            </Typography>

            <Typography variant="body2" sx={{ mb: 3 }} color="text.secondary">
              You’ll no longer receive marketing emails from us.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: "0.85rem" }}>
              Unsubscribed by accident?
            </Typography>

            <MDButton
              fullWidth
              color="success"
              disabled={saving}
              onClick={() => handleSave({ restoreAll: true })}
            >
              Opt In + Restore Preferences
            </MDButton>
          </Card>
        </Container>
      </>
    );
  }

  // Preferences View
  if (status === "success" && data?.data?.action === "preference" && !data?.data?.isGuest) {
    const isDirty =
      preferences &&
      initialPreferences &&
      editableKeys.some((key) => preferences.email[key] !== initialPreferences.email[key]);

    const isAllUnsubscribed =
      initialPreferences && editableKeys.every((key) => initialPreferences.email[key] === false);

    return (
      <>
        <Header />
        <Container>
          <Card
            sx={{
              maxWidth: 640,
              p: 4,
              borderRadius: 4,
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 3,
              }}
            >
              <MailIcon color="success" sx={{ fontSize: "48px !important" }} />
            </Box>

            <Typography variant="h5" fontWeight={600}>
              Manage Your Email Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Choose which types of emails you&apos;d like to receive.
            </Typography>

            {/* Unsubscribe All */}
            <MDButton
              variant="outlined"
              fullWidth
              color="success"
              sx={{ mb: 4 }}
              onClick={() => handleSave({ unsubscribeAll: true })}
              disabled={saving || isAllUnsubscribed}
            >
              Unsubscribe From All Emails
            </MDButton>

            {/* Preferences List */}
            {preferences && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: 600,
                    color: "grey.600",
                  }}
                >
                  <Typography variant="button">EMAIL TYPE</Typography>
                  <Typography variant="button">RECEIVE EMAILS</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {Object.keys(preferences.email)
                  .filter((key) => editableKeys.includes(key))
                  .map((key) => {
                    const value = preferences.email[key];
                    return (
                      <Card
                        key={key}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          textAlign: "left",
                          transition: "all 0.2s ease",
                          "&:hover": { backgroundColor: "grey.50" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {LABELS[key].title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {LABELS[key].description}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            <MDButton
                              variant={!value ? "contained" : "outlined"}
                              color="error"
                              size="small"
                              onClick={() => setPreference(key, false)}
                              disabled={saving || !value}
                            >
                              NO
                            </MDButton>
                            <MDButton
                              variant={value ? "contained" : "outlined"}
                              color="success"
                              size="small"
                              onClick={() => setPreference(key, true)}
                              disabled={saving || value}
                            >
                              YES
                            </MDButton>
                          </Box>
                        </Box>
                      </Card>
                    );
                  })}
              </>
            )}

            <MDButton
              fullWidth
              color="success"
              onClick={() => handleSave()}
              disabled={saving || !isDirty}
              sx={{ mt: 3 }}
            >
              {saving ? "Saving..." : "Save Preferences"}
            </MDButton>
          </Card>
        </Container>
      </>
    );
  }

  // Guest View
  if (status === "success" && data?.data?.action === "preference" && data?.data?.isGuest) {
    const isSuppressed = data.data.isSuppressed;

    return (
      <>
        <Header />
        <Container>
          <Card
            sx={{
              maxWidth: 480,
              p: 4,
              textAlign: "center",
              borderRadius: 4,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 3,
              }}
            >
              <MailIcon color="success" sx={{ fontSize: "48px !important" }} />
            </Box>

            <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
              {isSuppressed
                ? "You’ve been unsubscribed from marketing emails."
                : "You’re subscribed to marketing emails."}
            </Typography>

            {/* Subtext */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              You’ll still receive important transactional updates.
            </Typography>

            {/* Action Button */}
            <MDButton
              fullWidth
              color="success"
              disabled={saving}
              onClick={() =>
                handleSave(isSuppressed ? { restoreAll: true } : { unsubscribeAll: true })
              }
            >
              {isSuppressed ? "Opt In + Restore Preferences" : "Unsubscribe"}
            </MDButton>
          </Card>
        </Container>
      </>
    );
  }

  // Error View
  return (
    <>
      <Header />
      <Container>
        <Card
          sx={{
            maxWidth: 480,
            p: 4,
            textAlign: "center",
            borderRadius: 4,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <Typography variant="body1">{data?.message || "Something went wrong."}</Typography>
        </Card>
      </Container>
    </>
  );
}
